/**
 * ConfirmDialog Component
 * 
 * Reusable confirmation dialog for destructive or important actions
 * Supports different variants (danger, warning, info) with contextual styling
 * 
 * @component
 * @example
 * <ConfirmDialog
 *   open={isOpen}
 *   onClose={handleClose}
 *   onConfirm={handleConfirm}
 *   title="Remover Agent"
 *   message="Tem certeza que deseja remover este agent do grupo?"
 *   confirmText="Remover"
 *   variant="danger"
 * />
 */

import { FC, useEffect } from 'react';
import { FiAlertTriangle, FiInfo, FiAlertCircle, FiX } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import type { ConfirmDialogProps } from './ConfirmDialog.types';
import styles from './ConfirmDialog.module.css';

const ConfirmDialog: FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  isLoading = false,
}) => {
  /**
   * Get icon based on variant
   */
  const getIcon = () => {
    const iconProps = { size: 48 };
    
    switch (variant) {
      case 'danger':
        return <FiAlertCircle {...iconProps} className={styles.iconDanger} />;
      case 'warning':
        return <FiAlertTriangle {...iconProps} className={styles.iconWarning} />;
      case 'info':
        return <FiInfo {...iconProps} className={styles.iconInfo} />;
      default:
        return <FiAlertCircle {...iconProps} className={styles.iconDanger} />;
    }
  };

  /**
   * Handle ESC key press
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open && !isLoading) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose, isLoading]);

  /**
   * Handle backdrop click
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  /**
   * Handle confirm
   */
  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  if (!open) return null;

  return (
    <div 
      className={styles.backdrop} 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-message"
    >
      <div className={`${styles.dialog} ${styles[variant]}`}>
        {/* Close Button */}
        <button
          className={styles.closeButton}
          onClick={onClose}
          disabled={isLoading}
          aria-label="Fechar"
        >
          <FiX size={20} />
        </button>

        {/* Icon */}
        <div className={styles.iconContainer}>
          {getIcon()}
        </div>

        {/* Content */}
        <div className={styles.content}>
          <h2 id="confirm-dialog-title" className={styles.title}>
            {title}
          </h2>
          <p id="confirm-dialog-message" className={styles.message}>
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            disabled={isLoading}
            fullWidth
          >
            {cancelText}
          </Button>
          <Button
            variant={variant === 'info' ? 'primary' : 'danger'}
            size="md"
            onClick={handleConfirm}
            disabled={isLoading}
            fullWidth
          >
            {isLoading ? 'Processando...' : confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
